import { api } from '@/lib/api';
import type {
  BillType,
  BillStatus,
  TransactionCategory,
  MoneyDestination,
  TransactionStatus,
} from '@valiosascrm/shared';
import type { FinancialCategory } from '@valiosascrm/shared';

// Re-export shared types for backward compatibility
export type {
  BillType,
  BillStatus,
  TransactionCategory,
  MoneyDestination,
  TransactionStatus,
} from '@valiosascrm/shared';

// Single source of truth: Prisma enums via barrel (contract-S2).
// PaymentMethod values include 'boleto' (added 2026-05-25 per operator decision).
// Old drift: 'credit'/'debit' renamed to 'credit_card'/'debit_card' (Prisma canonical).
import type {
  PaymentMethod as PrismaPaymentMethod,
  TransactionType as PrismaTransactionType,
} from '@valiosascrm/shared/contract';
export type PaymentMethod = PrismaPaymentMethod;
export type TransactionType = PrismaTransactionType;

// Types
// Story 59.5 (AC8) [RC-052] — elegibilidade calculada no servidor pela mesma policy da edição.
export type EditableField =
  | 'amount'
  | 'paymentMethod'
  | 'installments'
  | 'destination'
  | 'category'
  | 'financialCategoryId'
  | 'description'
  | 'patientId'
  | 'paidAt'
  | 'targetRegisterId'
  | 'professionalId'
  | 'serviceId';

export interface TransactionMutability {
  editable: boolean;
  lockedFields: EditableField[];
  code?: string;
  message?: string;
  action?: { label: string; href?: string };
}

export interface ReceiptStanding {
  kind: 'planned' | 'partial';
  paidAmount: number;
  plannedOpenAmount: number;
  freeAmount: number;
}

export interface Transaction {
  id: string;
  mutability?: TransactionMutability;
  /** Story 65.22: só vem na cobrança com recebimento em partes paga em parte — o que já foi pago e o que falta. */
  paidAmount?: number;
  openAmount?: number;
  settlementModel?: 'legacy_v1' | 'receipt_v2';
  /** Story 65.65: só na cobrança no recebimento em partes em aberto — parcelada inteira ou com o que falta fora do parcelamento. */
  receiptStanding?: ReceiptStanding;
  type: TransactionType;
  category: TransactionCategory;
  financialCategoryId?: string | null;
  amount: number;
  // Story 44.7 [ACR-019] — bruto/taxa/líquido congelados no ato. Legado:
  // netAmount null = "sem taxa informada" (coalesce honesto, nunca inventar).
  installments?: number | null;
  feeAmount?: number;
  netAmount?: number | null;
  description: string;
  reference: string | null;
  paymentMethod: PaymentMethod;
  destination: MoneyDestination;
  appointmentId: string | null;
  patientId: string | null;
  // Story 67.12 (RC-213): quem atendeu e o serviço feito no recebimento avulso.
  serviceId?: string | null;
  professionalId?: string | null;
  cashRegisterId: string | null;
  originCashRegisterId?: string | null;
  receivableResponsibleId?: string | null;
  leftReceivableAt?: string | null;
  dueDate?: string | null;
  status: TransactionStatus;
  autoGenerated?: boolean;
  cancellationReason?: string | null;
  /** 65.17 — em revisão por serviço não realizado: fora das pendências ativas. */
  collectionHold?: string | null;
  createdById: string | null;
  paidAt?: string | null;
  deletedAt?: string | null;
  deletedById?: string | null;
  createdAt: string;
  financialCategory?: { id: string; name: string } | null;
  patient?: { id: string; name: string };
  appointment?: { id: string; startTime?: string; status?: string; service?: { name: string } };
  service?: { id: string; name: string } | null;
  createdBy?: { id: string; name: string } | null;
  receivableResponsible?: { id: string; name: string } | null;
  originCashRegister?: { id: string; openedAt: string; closedAt: string | null } | null;
}

// Story 44.8 [ACR-021] — breakdown do caixa por forma de pagamento (centavos).
// No caixa ABERTO vem calculado ao vivo pelo server (shape do register/current);
// no histórico é o SNAPSHOT congelado no fechamento (coluna methodTotals).
export interface MethodTotalEntry {
  method: PaymentMethod | null; // null = venda sem forma ("Não informado")
  count: number;
  gross: number;
  fee: number;
  net: number;
}

export interface CashRegister {
  id: string;
  openedAt: string;
  closedAt: string | null;
  openingBalance: number;
  closingBalance: number | null;
  expectedBalance: number | null;
  difference: number | null;
  notes: string | null;
  openedById: string;
  closedById: string | null;
  status: 'open' | 'closed';
  // Story 44.4 [ACR-017] — reabertura em modo correção paralelo.
  correctionMode?: boolean;
  reopenedAt?: string | null;
  reopenedById?: string | null;
  reopenReason?: string | null;
  reopenedBy?: { id: string; name: string } | null;
  /** Story 65.40 — só no histórico: a última reabertura foi a do mesmo dia ou a "para corrigir". */
  reopenKind?: 'same_day' | 'correction' | null;
  // Story 44.8 [ACR-021] — por forma: vivo no current, snapshot no histórico.
  methodTotals?: MethodTotalEntry[] | null;
  transactions?: Transaction[];
  // Story 65.22 — repasses postados neste caixa. Só vêm onde há recebimento em partes: a cobrança
  // em partes não está em `transactions`, o dinheiro dela entra por aqui.
  settlementTotals?: Array<{ method: string; label: string; settlementCount: number; grossAvailable: number; totalCostResolved: number; netAvailable: number; costPendingCount: number; refundPending: number }>;
  settlementCashIncome?: number;
}

export interface Bill {
  id: string;
  type: BillType;
  description: string;
  amount: number;
  dueDate: string;
  paidAt: string | null;
  paidAmount?: number | null;
  status: BillStatus;
  category: TransactionCategory;
  financialCategoryId?: string | null;
  settlementTransactionId?: string | null;
  patientId: string | null;
  reference: string | null;
  recurrence: string | null;
  notes: string | null;
  createdAt: string;
  financialCategory?: { id: string; name: string } | null;
  patient?: { id: string; name: string };
}

export interface FinancialGoal {
  id: string;
  month: number;
  year: number;
  targetRevenue: number;
  targetAppointments: number;
  currentRevenue: number;
  currentAppointments: number;
  createdAt: string;
}

export interface DailySummary {
  date: string;
  income: number;
  expense: number;
  balance: number;
  transactionCount: number;
}

export interface FinancialCategoryBreakdown {
  financialCategoryId: string | null;
  name: string | null;
  category: string;
  income: number;
  expense: number;
  count: number;
}

export interface MonthlySummary {
  year: number;
  month: number;
  income: number;
  expense: number;
  balance: number;
  realized?: { income: number; expense: number; balance: number; count: number };
  range?: { startDate: string; endDate: string; timezone: string };
  byCategory: Record<string, { income: number; expense: number }>;
  byFinancialCategory?: FinancialCategoryBreakdown[];
  byService?: Array<{ serviceId: string | null; name: string; income: number; expense: number; count: number }>;
  byProfessional?: Array<{ professionalId: string | null; name: string; income: number; expense: number; count: number }>;
  commitments?: { payableDue: number; receivableDue: number; overduePayable: number; overdueReceivable: number; count: number };
  goal: number;
  achieved: number;
  goalProgress: number;
}

export interface FinancialRegisterResolution {
  status: 'ready' | 'closed' | 'missing' | 'ambiguous';
  date: string;
  message: string;
  canCreateHistorical?: boolean;
  register?: Pick<CashRegister, 'id' | 'openedAt' | 'closedAt' | 'correctionMode'>;
  candidates: Array<Pick<CashRegister, 'id' | 'openedAt' | 'closedAt' | 'correctionMode'>>;
}

// Transactions API
export async function getTransactions(params?: {
  dateField?: 'createdAt' | 'paidAt';
  startDate?: string;
  endDate?: string;
  type?: TransactionType;
  category?: TransactionCategory;
  financialCategoryId?: string;
  financialCategoryIds?: string[];
  serviceIds?: string[];
  professionalIds?: string[];
  locationIds?: string[];
  status?: TransactionStatus;
  cashRegisterId?: string;
  excludeReceivables?: boolean;
  limit?: number;
  offset?: number;
  locationId?: string;
}): Promise<{ transactions: Transaction[]; total: number }> {
  const serializedParams = params
    ? {
        ...params,
        ...(params.financialCategoryIds
          ? { financialCategoryIds: params.financialCategoryIds.join(',') }
          : {}),
        ...(params.serviceIds ? { serviceIds: params.serviceIds.join(',') } : {}),
        ...(params.professionalIds
          ? { professionalIds: params.professionalIds.join(',') }
          : {}),
        ...(params.locationIds ? { locationIds: params.locationIds.join(',') } : {}),
      }
    : undefined;
  const { data } = await api.get<{ transactions: Transaction[]; total: number }>(
    '/cash-flow/transactions',
    { params: serializedParams },
  );
  return data;
}

export interface PatientCreditEntry {
  id: string;
  type: 'credit_added' | 'credit_used' | 'credit_adjusted';
  amount: number;
  description: string;
  createdAt: string;
  createdBy?: { id: string; name: string } | null;
}

export async function getPatientCredit(
  patientId: string,
): Promise<{ balance: number; entries: PatientCreditEntry[] }> {
  const { data } = await api.get(`/cash-flow/patients/${patientId}/credit`);
  return data;
}

export async function markTransactionPaid(
  id: string,
  data: {
    amount?: number;
    paymentMethod?: PaymentMethod;
    // 65.57: parcelas no crédito.
    installments?: number;
    destination?: MoneyDestination;
    paidAt?: string;
    targetRegisterId?: string;
    isCorrection?: boolean;
    createHistoricalRegister?: boolean;
    historicalRegisterReason?: string;
  },
): Promise<Transaction> {
  const { data: res } = await api.post<Transaction>(`/cash-flow/transactions/${id}/pay`, data);
  return res;
}

export async function cancelTransaction(id: string, reason: string): Promise<Transaction> {
  const { data } = await api.post<Transaction>(`/cash-flow/transactions/${id}/cancel`, {
    reason,
  });
  return data;
}

export async function deleteTransaction(id: string, reason: string): Promise<Transaction> {
  const { data } = await api.post<Transaction>(`/cash-flow/transactions/${id}/delete`, {
    reason,
  });
  return data;
}

// Story 59.3 — edição auditada: só os campos alterados + justificativa.
export interface EditTransactionInput {
  reason: string;
  amount?: number;
  paymentMethod?: PaymentMethod;
  installments?: number;
  category?: TransactionCategory;
  financialCategoryId?: string | null;
  description?: string;
  patientId?: string;
  // Story 67.12 (RC-213): nulo tira a profissional/o serviço do recebimento avulso.
  professionalId?: string | null;
  serviceId?: string | null;
  destination?: MoneyDestination;
  paidAt?: string;
  targetRegisterId?: string;
  isCorrection?: boolean;
  createHistoricalRegister?: boolean;
  historicalRegisterReason?: string;
}

// Story 59.6 (AC6) — a edição informa quando gerou/recalculou ajuste de comissão paga.
export interface EditTransactionResult extends Transaction {
  commissionChange?: {
    id: string;
    adjustment?: { id: string; adjustmentOf: string; mode: 'created' | 'updated' };
  };
}

export async function editTransaction(id: string, data: EditTransactionInput): Promise<EditTransactionResult> {
  const { data: updated } = await api.post<EditTransactionResult>(`/cash-flow/transactions/${id}/edit`, data);
  return updated;
}

export async function leaveReceivable(
  id: string,
  data: { responsibleUserId: string; dueDate: string },
): Promise<Transaction> {
  const { data: result } = await api.post<Transaction>(
    `/cash-flow/transactions/${id}/leave-receivable`,
    data,
  );
  return result;
}

export async function getReceivables(): Promise<Transaction[]> {
  const { data } = await api.get<Transaction[]>('/cash-flow/transactions/receivables');
  return data;
}

export async function getReceivableResponsibles(): Promise<Array<{ id: string; name: string }>> {
  const { data } = await api.get<Array<{ id: string; name: string }>>(
    '/cash-flow/transactions/receivable-responsibles',
  );
  return data;
}

export async function getTransaction(id: string): Promise<Transaction> {
  const { data } = await api.get<Transaction>(`/cash-flow/transactions/${id}`);
  return data;
}

export async function createTransaction(transactionData: {
  type: TransactionType;
  category: TransactionCategory;
  amount: number;
  description: string;
  paymentMethod: PaymentMethod;
  // 44.7 — parcelas da venda (Guard R2: só as com regra cadastrada).
  installments?: number;
  destination: MoneyDestination;
  reference?: string;
  appointmentId?: string;
  patientId?: string;
  professionalId?: string;
  serviceId?: string;
  markAsPaid?: boolean;
  paidAt?: string;
  dueDate?: string;
  financialCategoryId?: string;
  targetRegisterId?: string;
  isCorrection?: boolean;
  createHistoricalRegister?: boolean;
  historicalRegisterReason?: string;
}): Promise<Transaction> {
  const endpoint = transactionData.isCorrection || transactionData.createHistoricalRegister
    ? '/cash-flow/transactions/historical'
    : '/cash-flow/transactions';
  const { data } = await api.post<Transaction>(endpoint, transactionData);
  return data;
}

export async function resolveFinancialRegister(
  paidAt: string,
): Promise<FinancialRegisterResolution> {
  const { data } = await api.get<FinancialRegisterResolution>('/cash-flow/register/resolve', {
    params: { paidAt },
  });
  return data;
}

export async function getFinancialCategories(): Promise<FinancialCategory[]> {
  const { data } = await api.get<FinancialCategory[]>('/cash-flow/categories');
  return data;
}

export async function createFinancialCategory(name: string): Promise<FinancialCategory> {
  const { data } = await api.post<FinancialCategory>('/cash-flow/categories', { name });
  return data;
}

export async function getDailySummary(date: string): Promise<DailySummary> {
  const { data } = await api.get<DailySummary>(`/cash-flow/daily/${date}`);
  return data;
}

export type FinancialSummaryParams = Pick<
  NonNullable<Parameters<typeof getTransactions>[0]>,
  | 'startDate'
  | 'endDate'
  | 'category'
  | 'financialCategoryIds'
  | 'serviceIds'
  | 'professionalIds'
  | 'locationIds'
>;

export async function getMonthlySummary(
  year: number,
  month: number,
  params?: FinancialSummaryParams,
): Promise<MonthlySummary> {
  const serializedParams = params
    ? {
        ...params,
        ...(params.financialCategoryIds
          ? { financialCategoryIds: params.financialCategoryIds.join(',') }
          : {}),
        ...(params.serviceIds ? { serviceIds: params.serviceIds.join(',') } : {}),
        ...(params.professionalIds
          ? { professionalIds: params.professionalIds.join(',') }
          : {}),
        ...(params.locationIds ? { locationIds: params.locationIds.join(',') } : {}),
      }
    : undefined;
  const { data } = await api.get<MonthlySummary>('/cash-flow/summary/monthly', {
    params: { year, month, ...serializedParams },
  });
  return data;
}

// Cash Register API
export async function getOpenRegister(): Promise<CashRegister | null> {
  const { data } = await api.get<CashRegister | null>('/cash-flow/register/current');
  return data;
}

/** Story 65.40 — o que "abrir caixa" vai fazer agora: criar, reabrir o caixa fechado hoje, ou nada. */
export interface RegisterOpening {
  action: 'create' | 'reopen' | 'already_open' | 'blocked_correction';
  register?: { id: string; openedAt: string; closedAt: string | null; openingBalance: number };
}

export async function getRegisterOpening(): Promise<RegisterOpening> {
  const { data } = await api.get<RegisterOpening>('/cash-flow/register/opening');
  return data;
}

export async function openRegister(openingBalance: number): Promise<CashRegister> {
  const { data } = await api.post<CashRegister>('/cash-flow/register/open', { openingBalance });
  return data;
}

export async function closeRegister(closingBalance: number, notes?: string): Promise<CashRegister> {
  const { data } = await api.post<CashRegister>('/cash-flow/register/close', {
    closingBalance,
    notes,
  });
  return data;
}

export async function getRegisterHistory(params?: {
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}): Promise<{ registers: CashRegister[]; total: number }> {
  // 44.4: o backend devolve o array cru — normaliza pro shape { registers, total }.
  const { data } = await api.get<CashRegister[] | { registers: CashRegister[]; total: number }>(
    '/cash-flow/register/history',
    { params },
  );
  return Array.isArray(data) ? { registers: data, total: data.length } : data;
}

// Story 44.4 [ACR-017] — reabre caixa FECHADO em modo correção (motivo auditado).
export async function reopenRegister(id: string, reason: string): Promise<CashRegister> {
  const { data } = await api.post<CashRegister>(`/cash-flow/register/${id}/reopen`, { reason });
  return data;
}

// Story 44.4 — re-fechamento do caixa em correção (endpoint de close COM id).
export async function closeRegisterById(
  id: string,
  closingBalance: number,
  notes?: string,
): Promise<CashRegister> {
  const { data } = await api.post<CashRegister>(`/cash-flow/register/${id}/close`, {
    closingBalance,
    notes,
  });
  return data;
}

// Bills API
export async function getBills(params?: {
  financialCategoryIds?: string[];
  type?: BillType;
  status?: BillStatus;
  statuses?: BillStatus[];
  patientId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}): Promise<{ bills: Bill[]; total: number }> {
  const { data } = await api.get<{ bills: Bill[]; total: number }>('/cash-flow/bills', {
    params: {
      ...params,
      statuses: params?.statuses?.join(','),
      financialCategoryIds: params?.financialCategoryIds?.length ? params.financialCategoryIds.join(',') : undefined,
    },
  });
  return data;
}

export async function getBill(id: string): Promise<Bill> {
  const { data } = await api.get<Bill>(`/cash-flow/bills/${id}`);
  return data;
}

export async function createBill(billData: {
  type: BillType;
  description: string;
  amount: number;
  dueDate: string;
  category: TransactionCategory;
  financialCategoryId?: string;
  patientId?: string;
  reference?: string;
  recurrence?: string;
  notes?: string;
}): Promise<Bill> {
  const { data } = await api.post<Bill>('/cash-flow/bills', billData);
  return data;
}

export async function updateBill(
  id: string,
  billData: Partial<{
    description: string;
    amount: number;
    dueDate: string;
    category: TransactionCategory;
    financialCategoryId: string | null;
    reason: string;
    notes: string;
  }>,
): Promise<Bill> {
  const { data } = await api.patch<Bill>(`/cash-flow/bills/${id}`, billData);
  return data;
}

export async function markBillPaid(
  id: string,
  paymentData: {
    paymentMethod: PaymentMethod;
    destination: MoneyDestination;
    paidAmount?: number;
    receivedAmount?: number;
    keepAsCredit?: boolean;
    creditApplied?: number;
    paidAt?: string;
    targetRegisterId?: string;
    isCorrection?: boolean;
    createHistoricalRegister?: boolean;
    historicalRegisterReason?: string;
  },
): Promise<Bill> {
  const { data } = await api.post<Bill>(`/cash-flow/bills/${id}/pay`, paymentData);
  return data;
}

export async function cancelBill(id: string, reason: string): Promise<Bill> {
  const { data } = await api.post<Bill>(`/cash-flow/bills/${id}/cancel`, { reason });
  return data;
}

export async function getOverdueBills(): Promise<Bill[]> {
  const { data } = await api.get<Bill[]>('/cash-flow/bills/overdue');
  return data;
}

export async function getUpcomingBills(days?: number): Promise<Bill[]> {
  const { data } = await api.get<Bill[]>('/cash-flow/bills/upcoming', { params: { days } });
  return data;
}

// Goals API
export async function getMonthlyGoal(month: number, year: number): Promise<FinancialGoal | null> {
  const { data } = await api.get<FinancialGoal | null>(`/cash-flow/goals/${year}/${month}`);
  return data;
}

export async function setMonthlyGoal(goalData: {
  month: number;
  year: number;
  targetRevenue: number;
  targetAppointments: number;
}): Promise<FinancialGoal> {
  const { data } = await api.post<FinancialGoal>('/cash-flow/goals', goalData);
  return data;
}

export async function getGoalProgress(
  month: number,
  year: number,
): Promise<{
  target: { targetRevenue: number; targetAppointments: number } | null;
  current: { revenue: number; appointments: number };
  percentage: { revenue: number; appointments: number };
}> {
  const { data } = await api.get(`/cash-flow/goals/${year}/${month}/progress`);
  const goal = data.goal;
  return {
    target: goal
      ? {
          targetRevenue: goal.targetAmount ?? 0,
          targetAppointments: goal.targetAppointments ?? 0,
        }
      : null,
    current: data.current ?? { revenue: 0, appointments: 0 },
    percentage: {
      revenue: data.progress?.revenuePercentage ?? 0,
      appointments: data.progress?.appointmentsPercentage ?? 0,
    },
  };
}

// Category labels. Re-auditoria rodada 2 (P1) — o backend normaliza para o enum Prisma
// (products/equipment); o contrato compartilhado usa product/package/supplies. Este mapa é
// SUPERSET das duas grafias para que qualquer valor gravado (novo ou legado) tenha rótulo —
// senão a célula "Categoria" ficava em branco para Produto/Pacote (venda, fluxo core). O
// render usa fallback `?? cat` como última defesa.
export const CATEGORY_LABELS: Record<string, string> = {
  service: 'Serviço',
  // grafias do contrato compartilhado (dropdown) + canônicas do enum Prisma (persistência)
  product: 'Produto',
  package: 'Pacote',
  products: 'Produtos',
  equipment: 'Equipamentos',
  supplies: 'Insumos',
  rent: 'Aluguel',
  utilities: 'Contas (luz/água/internet)',
  salary: 'Salário',
  commission: 'Comissão',
  marketing: 'Marketing',
  other: 'Outros',
};

// Opções curadas para os dropdowns (subconjunto voltado ao usuário) — evita expor as
// grafias canônicas duplicadas do enum Prisma (products/equipment) usadas só na persistência.
export const CATEGORY_OPTIONS: Array<{ value: TransactionCategory; label: string }> = [
  { value: 'service', label: 'Serviço' },
  { value: 'product', label: 'Produto' },
  { value: 'package', label: 'Pacote' },
  { value: 'rent', label: 'Aluguel' },
  { value: 'utilities', label: 'Contas (luz/água/internet)' },
  { value: 'supplies', label: 'Insumos' },
  { value: 'salary', label: 'Salário' },
  { value: 'commission', label: 'Comissão' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'other', label: 'Outros' },
];

export const DESTINATION_LABELS: Record<MoneyDestination, string> = {
  bank: 'Banco',
  safe: 'Cofre',
  cash: 'Dinheiro',
};

// Fonte única de formas de pagamento (Story 44.10 / ADR-0001): lista canônica,
// labels e subconjuntos NOMEADOS vivem em @valiosascrm/shared
// (constants/payment-methods) — reexport pra compat dos consumidores atuais.
export {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHOD_SHORT_LABELS,
  APPOINTMENT_PAYMENT_METHODS,
  IN_PERSON_PAYMENT_METHODS,
} from '@valiosascrm/shared';
